<app-tab-toolbar [tabId]="'family'" [selectedYear]="selectedYear" [saveDisabled]="!hasChanges && !isFamilyDetailsCarriedForward"
  (yearChange)="onYearChange($event)" (save)="onSave()">
  @if (showYearWarning) {
    <div class="year-warning">
      <mat-icon>warning</mat-icon>
      @if (isFamilyDetailsCarriedForward) {
        <span>There is no data for the selected year. Some fields are shown from a previous year.</span>
      } @else {
        <span>There is no data for the selected year</span>
      }
    </div>
  }
</app-tab-toolbar>

<div class="family-content" [formGroup]="form" [appReadOnlyTab]="'family'">

  <!-- Founders Section -->
  <div class="columns-wrapper">
    <div class="column">
      <section class="section-container nrg-border-radius-second-level">
        <div class="section-header">
          <span>Founders</span>
        </div>
        <div class="section-content">
          <div class="founders-grid">
            @for (i of [1, 2, 3, 4, 5]; track i) {
              <label class="founder-label">Founder {{ i }}</label>
              <input type="text" class="ht-input founder-name-input" [formControlName]="'founder' + i" placeholder="Name" appTextNormalize>
            }
          </div>
        </div>
      </section>
    </div>

    <div class="column">
      <section class="section-container nrg-border-radius-second-level">
        <div class="section-header">
          <span>Company Info</span>
        </div>
        <div class="section-content">
          <div class="info-grid">
            <label>Year of Founding</label>
            <input type="text" class="ht-input ht-date-input" formControlName="yearOfFounding"
                   maxlength="10"
                   (focus)="onDateFocus($event)" (blur)="onDateBlur($event)"
                   (input)="onYearDateInput($event, 'yearOfFounding')">

            <label>Year went public</label>
            <input type="text" class="ht-input ht-date-input" formControlName="yearWentPublic"
                   maxlength="10"
                   (focus)="onDateFocus($event)" (blur)="onDateBlur($event)"
                   (input)="onYearDateInput($event, 'yearWentPublic')">

            <label>Founding Family</label>
            <input type="text" class="ht-input" formControlName="foundingFamily" appTextNormalize>

            <label>Founders Name</label>
            <mat-checkbox formControlName="foundersName"></mat-checkbox>
          </div>
        </div>
      </section>
    </div>
  </div>

  <!-- Board of Directors / Supervisory Board -->
  <div class="board-section">
    <h3 class="section-title">{{ companyBoardType === 0 ? 'Board of Directors' : 'Supervisory Board' }}</h3>

    @if (boardDataSource.data.length === 0) {
      <div class="no-data-message">No data available</div>
    } @else {
    <div class="table-wrapper">
      <table mat-table [dataSource]="boardDataSource" class="family-table" formArrayName="boardRows" matSort #boardSort="matSort" matSortActive="name" matSortDirection="asc" (matSortChange)="onBoardSort($event)">

        <ng-container matColumnDef="index">
          <th mat-header-cell *matHeaderCellDef>#</th>
          <td mat-cell *matCellDef="let row; let i = index" class="cell-index" (click)="toggleRowHighlight(row)">{{ i + 1 }}</td>
        </ng-container>

        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Full Name</th>
          <td mat-cell *matCellDef="let row">{{ row.directorFullName }}</td>
        </ng-container>

        <ng-container matColumnDef="percentage">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>%</th>
          <td mat-cell *matCellDef="let row" class="text-right">{{ formatPercentage(row.firstClassSharesPercentage) }}</td>
        </ng-container>

        <ng-container matColumnDef="classification">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Classification</th>
          <td mat-cell *matCellDef="let row">{{ getClassificationName(row.classification) }}</td>
        </ng-container>

        <ng-container matColumnDef="occupation">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Occupation</th>
          <td mat-cell *matCellDef="let row">{{ getOccupationName(row.occupationId) }}</td>
        </ng-container>

        <ng-container matColumnDef="familyStatus">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Family Status</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="familyCompanyStatusId" class="ht-input ht-select">
              <option [ngValue]="null"></option>
              @for (status of familyStatuses; track status.id) {
                <option [ngValue]="status.id">{{ status.name }}</option>
              }
            </select>
          </td>
        </ng-container>

        <ng-container matColumnDef="generation">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Generation</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="familyGenerationId" class="ht-input ht-select">
              <option [ngValue]="null"></option>
              @for (gen of familyGenerations; track gen.id) {
                <option [ngValue]="gen.id">{{ gen.name }}</option>
              }
            </select>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="boardDisplayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: boardDisplayedColumns;" [class.row-highlighted]="row.highlighted"></tr>
      </table>
    </div>
    }
  </div>

  <!-- Management Board (Dual mode only) -->
  @if (companyBoardType !== 0) {
    <div class="board-section">
      <h3 class="section-title">Management Board</h3>

      @if (managementDataSource.data.length === 0) {
        <div class="no-data-message">No data available</div>
      } @else {
      <div class="table-wrapper">
        <table mat-table [dataSource]="managementDataSource" class="family-table" formArrayName="managementRows" matSort #managementSort="matSort" matSortActive="name" matSortDirection="asc" (matSortChange)="onManagementSort($event)">

          <ng-container matColumnDef="index">
            <th mat-header-cell *matHeaderCellDef>#</th>
            <td mat-cell *matCellDef="let row; let i = index" class="cell-index" (click)="toggleRowHighlight(row)">{{ i + 1 }}</td>
          </ng-container>

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Full Name</th>
            <td mat-cell *matCellDef="let row">{{ row.directorFullName }}</td>
          </ng-container>

          <ng-container matColumnDef="percentage">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>%</th>
            <td mat-cell *matCellDef="let row" class="text-right">{{ formatPercentage(row.firstClassSharesPercentage) }}</td>
          </ng-container>

          <ng-container matColumnDef="classification">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Classification</th>
            <td mat-cell *matCellDef="let row">{{ getClassificationName(row.classification) }}</td>
          </ng-container>

          <ng-container matColumnDef="occupation">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Occupation</th>
            <td mat-cell *matCellDef="let row">{{ getOccupationName(row.occupationId) }}</td>
          </ng-container>

          <ng-container matColumnDef="familyStatus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Family Status</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <select formControlName="familyCompanyStatusId" class="ht-input ht-select">
                <option [ngValue]="null"></option>
                @for (status of familyStatuses; track status.id) {
                  <option [ngValue]="status.id">{{ status.name }}</option>
                }
              </select>
            </td>
          </ng-container>

          <ng-container matColumnDef="generation">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Generation</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <select formControlName="familyGenerationId" class="ht-input ht-select">
                <option [ngValue]="null"></option>
                @for (gen of familyGenerations; track gen.id) {
                  <option [ngValue]="gen.id">{{ gen.name }}</option>
                }
              </select>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="boardDisplayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: boardDisplayedColumns;" [class.row-highlighted]="row.highlighted"></tr>
        </table>
      </div>
      }
    </div>
  }

  <!-- Executive Management -->
  <div class="board-section">
    <h3 class="section-title">Executive Management</h3>

    @if (executiveDataSource.data.length === 0) {
      <div class="no-data-message">No data available</div>
    } @else {
    <div class="table-wrapper">
      <table mat-table [dataSource]="executiveDataSource" class="family-table" formArrayName="executiveRows" matSort #executiveSort="matSort" matSortActive="name" matSortDirection="asc" (matSortChange)="onExecutiveSort($event)">

        <ng-container matColumnDef="index">
          <th mat-header-cell *matHeaderCellDef>#</th>
          <td mat-cell *matCellDef="let row; let i = index" class="cell-index" (click)="toggleRowHighlight(row)">{{ i + 1 }}</td>
        </ng-container>

        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Full Name</th>
          <td mat-cell *matCellDef="let row">{{ row.directorFullName }}</td>
        </ng-container>

        <ng-container matColumnDef="percentage">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>%</th>
          <td mat-cell *matCellDef="let row" class="text-right">{{ formatPercentage(row.firstClassSharesPercentage) }}</td>
        </ng-container>

        <ng-container matColumnDef="occupation">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Occupation</th>
          <td mat-cell *matCellDef="let row">{{ getOccupationName(row.occupationId) }}</td>
        </ng-container>

        <ng-container matColumnDef="familyStatus">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Family Status</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="familyCompanyStatusId" class="ht-input ht-select">
              <option [ngValue]="null"></option>
              @for (status of familyStatuses; track status.id) {
                <option [ngValue]="status.id">{{ status.name }}</option>
              }
            </select>
          </td>
        </ng-container>

        <ng-container matColumnDef="generation">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Generation</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="familyGenerationId" class="ht-input ht-select">
              <option [ngValue]="null"></option>
              @for (gen of familyGenerations; track gen.id) {
                <option [ngValue]="gen.id">{{ gen.name }}</option>
              }
            </select>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="executiveDisplayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: executiveDisplayedColumns;" [class.row-highlighted]="row.highlighted"></tr>
      </table>
    </div>
    }
  </div>

  <!-- Large Shareholders -->
  <div class="board-section">
    <h3 class="section-title">Large Shareholders</h3>

    @if (isLargeShareholdersCarriedForward) {
      <div class="year-warning">
        <mat-icon>info</mat-icon>
        <span>Large shareholders carried forward from a previous year.</span>
      </div>
    }

    @if (shareholderDataSource.data.length === 0 && !isLargeShareholdersCarriedForward) {
      <div class="no-data-message">No data available</div>
    } @else {
    <div class="table-wrapper">
      <table mat-table [dataSource]="shareholderDataSource" class="family-table" formArrayName="shareholderRows" matSort #shareholderSort="matSort" matSortActive="name" matSortDirection="asc" (matSortChange)="onShareholderSort($event)">

        <ng-container matColumnDef="index">
          <th mat-header-cell *matHeaderCellDef>#</th>
          <td mat-cell *matCellDef="let row; let i = index" class="cell-index" (click)="toggleRowHighlight(row)">{{ i + 1 }}</td>
        </ng-container>

        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Full Name</th>
          <td mat-cell *matCellDef="let row">{{ row.largeShareholderName }}</td>
        </ng-container>

        <ng-container matColumnDef="percentage">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>%</th>
          <td mat-cell *matCellDef="let row" class="text-right">{{ formatPercentage(row.firstClassSharesPercentage) }}</td>
        </ng-container>

        <ng-container matColumnDef="category">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Category</th>
          <td mat-cell *matCellDef="let row">{{ row.categoryName }}</td>
        </ng-container>

        <ng-container matColumnDef="subCategory">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Sub-Category</th>
          <td mat-cell *matCellDef="let row">{{ row.subCategoryName }}</td>
        </ng-container>

        <ng-container matColumnDef="familyStatus">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Family Status</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="familyCompanyStatusId" class="ht-input ht-select">
              <option [ngValue]="null"></option>
              @for (status of familyStatuses; track status.id) {
                <option [ngValue]="status.id">{{ status.name }}</option>
              }
            </select>
          </td>
        </ng-container>

        <ng-container matColumnDef="generation">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Generation</th>
          <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
            <select formControlName="familyGenerationId" class="ht-input ht-select">
              <option [ngValue]="null"></option>
              @for (gen of familyGenerations; track gen.id) {
                <option [ngValue]="gen.id">{{ gen.name }}</option>
              }
            </select>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="shareholderDisplayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: shareholderDisplayedColumns;" [class.row-highlighted]="row.highlighted"></tr>
      </table>
    </div>
    }
  </div>

  <!-- Comments -->
  <section class="section-container nrg-border-radius-second-level comments-section">
    <div class="section-header">
      <span>Comments</span>
    </div>
    <div class="section-content">
      <mat-form-field appearance="fill" class="form-field">
        <mat-label>Comments</mat-label>
        <textarea matInput formControlName="familyComments" rows="3"></textarea>
      </mat-form-field>
    </div>
  </section>

</div>
